import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Observable, catchError, of, timeout } from "rxjs";
import { ApiResponse } from "../interfaces/api-response";

/**
 * Una novedad publicada de la plataforma (versión + título + lista de
 * cambios), incluyendo si el local autenticado ya la leyó.
 */
export interface ChangelogEntry {
  id: number;
  version: string;
  title: string;
  changes: string[];
  publishedAt: string;
  read: boolean;
}

export interface ChangelogListResult {
  entries: ChangelogEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateChangelogEntryPayload {
  version: string;
  title: string;
  changes: string[];
}

export interface ChangelogPublishResult {
  entry: ChangelogEntry;
  emailsSent: number;
  emailsFailed: number;
}

/**
 * Único punto de acceso HTTP al dominio de novedades de la plataforma
 * (`GET /changelog`, `POST /changelog/read`, `POST /platform/changelog`).
 * Lo usan tanto la campanita de notificaciones (lectura + marcar leído,
 * cualquier local) como la pantalla de superadmin en Plataforma
 * (publicación, solo superadmin).
 */
@Injectable({ providedIn: "root" })
export class ChangelogService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private _httpClient: HttpClient) {}

  /**
   * Lista las novedades publicadas, de la más reciente a la más antigua,
   * con el estado de lectura real del local autenticado. Un fallo de red
   * no debe romper `initialDataResolver` (que también carga mensajes,
   * navegación, chat rápido y atajos), así que se degrada a una lista
   * vacía en vez de propagar el error.
   */
  get(page = 1, limit = 20): Observable<ApiResponse<ChangelogListResult>> {
    return this._httpClient
      .get<ApiResponse<ChangelogListResult>>(
        `${this.apiUrl}/changelog?page=${page}&limit=${limit}`,
      )
      .pipe(
        timeout(8000),
        catchError(() =>
          of(
            new ApiResponse<ChangelogListResult>(false, "", {
              entries: [],
              total: 0,
              page,
              limit,
            }, 0),
          ),
        ),
      );
  }

  /** Publica una novedad. Solo superadmin (`SuperAdminGuard`). */
  create(
    payload: CreateChangelogEntryPayload,
  ): Observable<ApiResponse<ChangelogPublishResult>> {
    return this._httpClient.post<ApiResponse<ChangelogPublishResult>>(
      `${this.apiUrl}/platform/changelog`,
      payload,
    );
  }

  /**
   * Marca todas las novedades como leídas para el local autenticado. Igual
   * que `get()`, no debe romper el arranque del panel si falla.
   */
  markAllAsRead(): Observable<ApiResponse<null>> {
    return this._httpClient
      .post<ApiResponse<null>>(`${this.apiUrl}/changelog/read`, {})
      .pipe(
        timeout(8000),
        catchError(() => of(new ApiResponse<null>(false, "", null, 0))),
      );
  }
}
